/**
 * **역방향 순회 도구 — 사고 대응용** (PR 7-b, 설계 `backend/docs/design/vault_sweep_keys/` K6).
 *
 * v2 로 쓴 값을 v1 으로 되돌린다. 정방향 러너를 그대로 쓰고 **방향 서술자만 바꾼다.**
 *
 * 🛑 **UI 로 노출하지 않는다.** 사고 대응은 드물고 신중해야 하는 작업이라 버튼은 오클릭
 *    위험이 있다. 콘솔 진입점 `window.__vaultReverse` 로만 닿는다.
 *
 * 🛑 **운영 빌드에서도 붙인다** (2026-08-27 정정 — 초판은 `isDevBuild` 로 막았다).
 *    되돌리기가 반드시 필요한 상황은 하나뿐인데 그것이 **운영에서 배포를 revert 할 때**다:
 *    v2 판독기가 빠지면 이미 v2 인 값을 못 읽으므로 **값을 먼저 v1 으로 되돌린 뒤에** 코드를
 *    되돌려야 한다. 열쇠는 언락한 브라우저에만 있어(제로 지식) 서버 배치로 대체할 수 없다.
 *    로드맵 §6-③ 이 *"'롤백 계획'이 아니라 '롤백 도구'가 있어야 한다"* 고 못박은 것이 이
 *    진입점이며, dev 에만 두면 **정작 필요한 곳에 없다.**
 *
 * 🛑 **이 파일은 `sweepRunner` 가 import 하면 안 된다.** `pUtils` 를 끌어오는데 그 체인이
 *    `env → service → conf → window` 라 러너의 node 환경 테스트를 로드 단계에서 죽인다.
 *    의존 방향은 항상 `reverseTool → sweepRunner` 한 쪽이다.
 *
 * 🛑 **실행 전에 `VAULT_V2_SWEEP_KEYS_WORKSPACE_IDS` 를 먼저 비운다** (spec §5.3).
 *    플래그가 켜진 채로 되돌리면 **다음 언락에 정방향이 다시 v2 로 만든다** — 되돌리기가
 *    무효가 된다. 자동 경로(K8 재조회 실패)는 같은 세션에서 중단하므로 이 문제가 없다.
 */
import forge from 'node-forge'
import ResourceService from 'api/Resource'
import { encryptAESByKey } from 'pUtils'
import { getTempEnvParam } from 'env'
import { VAULT_WRITE_FORMAT_KEY } from 'vaultWriteFormat'
import { isVaultV2 } from './format'
import { rewriteV2ToV1 } from './reverseSweep'
import { startReverseSweep } from './sweepRunner'
import { makeUserKeySource, makeGroupKeySourceFrom } from './keyAdapters'

/**
 * 키 재료 접근을 한 번 정규화한다.
 *
 * 🛑 **리소스와 열쇠가 서로 다른 이름으로 재료를 들고 있다** — 리소스는
 * `rsKeyObj.rsKey`(`pUtils.js:1381`), 열쇠는 `keyCtx.rawMaterial`(`keyAdapters.js:50`).
 * 정방향은 메서드(`encrypt`/`decrypt`)만 쓰므로 이 차이가 안 보이지만, 역방향은
 * `rewriteV2ToV1` 이 **재료 자체**를 요구해서 드러난다.
 */
const materialOf = (keyObj) => keyObj?.rawMaterial ?? keyObj?.rsKey ?? null

/**
 * 역방향 서술자.
 *
 * 🛑 **세 필드를 전부 바꾼다.** 하나만 바꾸면 조용한 무동작이 된다(spec G5) —
 *    `gate` 를 정방향 것으로 두면 **닫혀서 시작조차 못 하고**, `hasWork` 를 두면
 *    **전 레코드를 건너뛴다.**
 */
export const REVERSE = {
  // 정방향 게이트는 `writeFormat === 2` 를 요구한다. 되돌리는 중에는 그 값이 무엇이든
  // 돌아야 하므로 항상 연다 — 무엇을 되돌릴지는 아래 `hasWork` 가 정한다.
  gate: () => true,
  hasWork: (t) => isVaultV2(forge.util.decode64(t.content)),
  // 🛑 **thunk 는 byte string 을 돌려줘야 한다** — `rewriteV2ToV1:63` 이 `encode64` 한다.
  //    base64 를 넘기면 두 번 씌워져 **어떤 키로도 복호되지 않는 값**이 저장된다
  //    (실측: `v1 decrypt failed`). 되돌릴 수 없는 유일한 실패다.
  //
  //    🛑 **이 thunk 를 다른 곳에 복제하지 않는다** (PR #2017 리뷰 minor). 한때
  //    `keyAdapters.makeKeyCtx` 에도 같은 것이 있었는데 프로덕션 호출자가 0곳이라
  //    죽은 채로 계약만 둘이 됐다 — 한쪽만 고치면 그 함정이 그대로 재발한다.
  convert: async (blobBase64, keyObj) => {
    const material = materialOf(keyObj)
    if (!material) throw new Error('reverse_sweep_no_key_material')
    return rewriteV2ToV1(blobBase64, material, (plain) => encryptAESByKey(plain, material))
  },
}

/**
 * 역방향을 실행한다 — **인자 없이 부를 수 있다.**
 *
 * 🛑 **deps 를 스스로 조립한다.** 초판은 `deps` 를 받아 펼치기만 했는데, 콘솔에서는 그
 * deps 를 만들 수 없다 — `api/Resource`·`keyAdapters`·`env` 가 번들 안에 있고 `window` 에
 * 노출된 것은 이 함수뿐이기 때문이다(실측: `grep -rn "window\.__" src` → 이 파일과 사가
 * 주석뿐). **도구가 있다고 믿었는데 실제로는 못 쓰는 상태였다.** 조립 내용은
 * `redux/user/sagas.js` 의 정방향 호출부와 같다.
 *
 * 🛑 **`sweepKeys` 를 true 로 고정한다 — 서버가 준 값을 쓰지 않는다.** 역방향을 돌리기
 * 전에 `VAULT_V2_SWEEP_KEYS_WORKSPACE_IDS` 를 비우는 것이 절차인데(spec §5.3), 그러면
 * 서버가 `sweep_keys: false` 를 내려보낸다. 그 값을 그대로 쓰면 **되돌리기도 열쇠를
 * 건너뛴다.** 플래그는 *정방향을 막는* 스위치이지 사고 대응까지 막으면 안 된다.
 *
 * @param {object} [over] 콘솔에서 일부만 덮어쓸 때. `direction` 은 덮을 수 없다
 */
export const runReverseSweep = (over = {}) => {
  const rsaObj = getTempEnvParam('rsaObj')
  return startReverseSweep({
    resourceService: ResourceService,
    rewriteService: { rewrite: (targets) => ResourceService.rewriteVaultFormat(targets) },
    writeFormat: getTempEnvParam(VAULT_WRITE_FORMAT_KEY),
    sweepKeys: true,
    userKeySource: makeUserKeySource(rsaObj),
    makeGroupKeySource: makeGroupKeySourceFrom(rsaObj),
    ...over,
    // 🛑 `over` 뒤다 — 방향은 이 도구의 정체성이라 덮을 수 없어야 한다.
    direction: REVERSE,
  })
}

if (typeof window !== 'undefined') {
  // 밑줄 두 개는 **의도적이다** — 앱 코드가 쓰는 이름이 아니라 콘솔 전용 진입점이라는 표시다.
  // eslint-disable-next-line no-underscore-dangle
  window.__vaultReverse = runReverseSweep
}

export default runReverseSweep
